/* ===========================================================================
   hcf-mockup-builder.js
   ---------------------------------------------------------------------------
   Drives the free digital mockup request flow.

   THE SHAPE OF THE THING
   Three screens the customer fills in, then a confirmation:

     1. Which cup do you want to see it on?   (section blocks, then Continue)
     2. What are you going for?               (style chips + optional notes
                                               + the pricing opt-in)
     3. Your logo and where to send it        (HubSpot embedded form)
     4. Confirmation                          (recap + the escalation to a quote)

   WHY SCREEN 1 HAS A CONTINUE BUTTON RATHER THAN AUTO-ADVANCING
   It used to advance on its own 180ms after a tile was tapped. That is a good
   pattern for a short list of words and a bad one for a grid of photographs:
   people compare photographs, and a screen that leaves the instant you touch
   something punishes exactly that. It also removed the beat where somebody
   confirms to themselves that they picked the right cup, with no way back once
   the screen had gone.

   WHY SCREEN 3 IS A HUBSPOT FORM AND NOT OURS
   A Shopify storefront cannot accept a file upload. There is no native form
   that takes an attachment, the Files API is Admin-only, and posting to
   HubSpot's v3 submissions endpoint takes a file URL, not a file. Embedding
   HubSpot's own form is the one route that gets the logo off the customer's
   machine and onto the contact record without adding a third-party uploader.

   WHY THE FORM IS MOUNTED ON SCREEN TWO
   The embed is a portal script plus a cross-origin iframe which then fetches
   the form definition, its field list and its own stylesheet. Starting all of
   that at the moment somebody arrives on screen 3 puts a visible pause at the
   worst point in the flow - the step where they are being asked to hand
   something over.

   So the mount happens when they LEAVE screen 1. Screen 3 is still `hidden` at
   that point, but hidden is display:none, not "not in the document", and the
   iframe loads regardless of whether it is painted.

   Measured on this store, with the script and frame already in browser cache,
   the form fires its ready event about 0.35s after the Continue on screen 1. A
   cold first visit is slower - the second or so that prompted this change -
   and either way it now happens while the customer is picking a style rather
   than while they are staring at the upload field. The skeleton is the safety
   net for the case where it has not finished in time. Do not read the 0.35s as
   a budget; it is a warm-cache figure, not a cold one.

   ONE KNOWN WRINKLE. HubSpot sizes its iframe by measuring the child document
   and pushing a height onto .hs-form-frame. A document inside display:none has
   no layout to measure, so the height it first reports is 0 and the form is
   only given its real height once the screen is shown and the browser lays it
   out. That resolves on its own in a foreground tab. It does NOT resolve while
   the whole tab is backgrounded, which is worth knowing before anyone debugs
   this from a devtools window in another window and concludes the form is
   broken. It is not - bring the tab forward.

   =========================================================================
   THE EVENT NAMES. READ ALL OF THIS BEFORE TOUCHING A LISTENER.
   =========================================================================
   The portal embed bundle dispatches exactly SIX events on `window`. Read out
   of the shipped bundle, in full:

     hs-form-event:on-ready
     hs-form-event:on-submission:success
     hs-form-event:on-submission:failed
     hs-form-event:on-interaction:navigate
     hs-form-event:on-interaction:navigate:next
     hs-form-event:on-interaction:navigate:previous

   This cost two wrong guesses, and both are worth recording because the next
   person will make the same ones.

   FIRST WRONG GUESS: `hs-form-event:on-submitted`, which is plausible and is
   what several HubSpot docs pages imply. It does not exist.

   SECOND WRONG GUESS: `hs-form-event:on-submission`. That one came from
   grepping the bundle with a regex that stopped at the second colon:

     /hs-form-event:[a-z-]+/g          <-- WRONG, truncates the name
     /hs-form-event:[a-z:-]+/g         <-- right, the names contain colons

   The names are namespaced three deep. A regex that does not allow a colon in
   the tail silently hands you a prefix that looks exactly like a real event
   name and matches nothing.

   The symptom of both was identical and quiet: the form submitted fine, the
   contact record updated correctly, the ticket was created, and the customer
   sat looking at HubSpot's own grey "Form submitted" panel inside our card,
   because our confirmation screen was waiting on an event that never came.

   To re-derive after any HubSpot release, run this in the console on this page
   and note the character class:

     fetch('https://js.hsforms.net/forms/embed/<PORTAL>.js')
       .then(r => r.text())
       .then(t => console.log([...new Set(t.match(/hs-form-event:[a-z:-]+/g))]));

   =========================================================================

   HOW THE ANSWERS FROM SCREENS 1 AND 2 GET ONTO THAT FORM
   Not by writing into its DOM. HubSpot's current forms embed renders inside a
   CROSS-ORIGIN IFRAME - js.hsforms.net/ui-forms-embed-components-app/frame -
   so there is nothing in this document to write to. Verified: zero light-DOM
   inputs, no shadow root, nothing named mockup_* anywhere on the page.

   Instead we use HubSpot's own V4 form API, which talks across that boundary
   for us. `window.HubspotFormsV4` exposes exactly three things: getForms,
   getFormFromEvent, and a __SECRET_INTERNAL_DO_NOT_USE we leave alone.

     window.addEventListener('hs-form-event:on-ready', e => {
       const form = HubspotFormsV4.getFormFromEvent(e);
       form.setFieldValue(name, value);
     });

   Because the mount happens BEFORE the last of the answers exist, the sync
   runs twice: once when the form reports ready, and again when the customer
   actually advances to screen 3. `state` is a live object, so both reads see
   whatever is current.

   Confirmed end to end on a real submission: cup type, style direction, wants
   pricing and quantity all arrived on the contact record with the right
   values, and the uploaded logo arrived on `mockup_logo_upload` as a signed
   file URL. Note that property's real internal name - it is
   `mockup_logo_upload`, not `mockup_logo`.

   TWO THINGS THAT WILL BITE ANYONE EDITING THIS
   1. Field names are OBJECT-PREFIXED. The form does not know a field called
      `mockup_cup_type`; it knows `0-1/mockup_cup_type`, where 0-1 is HubSpot's
      internal id for Contact (0-2 is Company). setFieldValue with the bare
      name fails SILENTLY - no error, no value. Rather than hardcode the
      prefix we read the real names back with getFormFieldValues() and match on
      the part after the slash, so a change of prefix cannot break this.
   2. The V4 getters are ASYNC. getFieldValue and getFormFieldValues return
      promises. Reading one without awaiting gives you a Promise object, which
      stringifies to {} and looks exactly like an empty field.

   THE DOM ON SCREEN 3, AND WHY IT IS THREE ELEMENTS
     #mbFormCard    the white card. Ours.
     #mbFormTarget  the bare mount point. HubSpot's iframe is APPENDED here, so
                    nothing of ours may live inside it or the form would land
                    underneath it.
     .mb-formnote   the fine print under the submit button. Ours.
   showFallback() therefore hides the CARD, not the mount point: hiding only
   the mount point would leave an empty white box holding nothing but legal
   text next to the phone number.

   HubSpot's own "Form submitted" panel is never seen by a customer, and is not
   suppressed either - it renders inside the iframe, which we cannot reach. It
   is simply that screen 3 goes display:none the instant the success event
   fires, and the iframe goes with it.

   SAFE WITHOUT HUBSPOT
   With no portal or form ID configured the section still renders and still
   walks all four screens. Screen 3 shows the email/phone fallback instead of a
   form and the console says why once. Same if the embed script fails or never
   renders. The page is linked from the home page, and a page that throws is
   worse than a page that tells you to call.

   STATE
   Kept in one object and mirrored to localStorage on every change, so a
   customer who wanders off mid-flow comes back to what they picked. Cleared on
   successful submit - a stale cup selection is worse than an empty form.
   =========================================================================== */

(function () {
  'use strict';

  var ROOT = document.querySelector('[data-mockup-builder]');
  if (!ROOT) return;

  /* The storage key is versioned. Bumping the number is how we throw away
     everybody's saved state after a change to the shape of it, rather than
     trying to migrate a blob nobody can see. */
  var STORE_KEY = 'hcf_mockup_v1';

  var SCREENS = ['m1', 'm2', 'm3', 'm4'];

  /* Screen 4 is the confirmation, not a step the customer works through, so
     the rail shows three. */
  var RAIL_LABELS = ['Your cup', 'Your look', 'Your logo'];

  /* The three HubSpot events this file acts on, named in full. See the block
     at the top of the file before changing any of these strings. */
  var HS_EVENT_READY = 'hs-form-event:on-ready';
  var HS_EVENT_SUBMIT_OK = 'hs-form-event:on-submission:success';
  var HS_EVENT_SUBMIT_FAIL = 'hs-form-event:on-submission:failed';

  var state = {
    cup: null,        // slug of the chosen cup family
    cupLabel: '',     // its human label, kept so the recap needs no lookup
    vibe: null,
    notes: '',
    wantsPricing: false,
    qty: null,
    screen: 'm1'
  };

  function warn(msg, err) {
    if (window.console && window.console.warn) {
      window.console.warn('[hcf-mockup-builder] ' + msg, err || '');
    }
  }

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------

  /* Every read and write is wrapped. Safari in private mode throws on
     localStorage.setItem rather than failing quietly, and an exception here
     would take the whole builder down over a convenience feature. */
  function save() {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) { /* no-op: saving is a nicety, not a requirement */ }
  }

  function restore() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return;
      var saved = JSON.parse(raw);
      if (!saved || typeof saved !== 'object') return;
      // Copy field by field so a stale key from an older shape cannot get in.
      if (typeof saved.cup === 'string') state.cup = saved.cup;
      if (typeof saved.cupLabel === 'string') state.cupLabel = saved.cupLabel;
      if (typeof saved.vibe === 'string') state.vibe = saved.vibe;
      if (typeof saved.notes === 'string') state.notes = saved.notes;
      if (typeof saved.wantsPricing === 'boolean') state.wantsPricing = saved.wantsPricing;
      if (typeof saved.qty === 'string') state.qty = saved.qty;
    } catch (e) { /* corrupt blob: start clean rather than guess */ }
  }

  function clearSaved() {
    try { window.localStorage.removeItem(STORE_KEY); } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Small DOM helpers
  // ---------------------------------------------------------------------------

  function $(sel) { return ROOT.querySelector(sel); }
  function $$(sel) { return Array.prototype.slice.call(ROOT.querySelectorAll(sel)); }

  // ---------------------------------------------------------------------------
  // Screen navigation
  // ---------------------------------------------------------------------------

  /* NOTE FOR ANYONE CALLING THIS: show() SAVES. Every call writes the current
     state to localStorage, which means clearSaved() must come AFTER any show()
     that follows it, never before. See the submit handler.

     opts.noScroll is for the very first call, at init.

     Every later call is a deliberate move between screens, and bringing the
     new screen up to the top is what stops somebody landing halfway down a
     screen they have not read the top of. The FIRST call is different: it is
     just the page rendering, nobody has done anything yet, and scrolling then
     shoves the announcement bar, the logo and the whole nav off the top of a
     page the visitor has only just arrived on. It reads as the page jumping. */
  function show(id, opts) {
    /* `opts.initial` marks the single call made at init. It turns off BOTH the
       focus move and the scroll below - see the comments on each. */
    var initial = !!(opts && opts.initial);

    state.screen = id;
    save();

    SCREENS.forEach(function (s) {
      var el = $('#s-' + s);
      if (el) el.hidden = (s !== id);
    });

    renderRail();

    /* Focus mode: from screen two onward this page is a form, and the rest of
       the template is competing content aimed at somebody browsing. Same
       pattern as the quote builder, with its own body class so the two do not
       hide each other. */
    document.body.classList.toggle('hcf-mb-focus', id !== 'm1');

        /* Nothing below this line runs on first paint. Both the focus move and the
       scroll are correct for a screen CHANGE and wrong for a page that has
       merely finished loading. */
    if (initial) return;

    /* FOCUS. Moving focus to the new screen's heading is what makes a screen
       change audible - the URL does not change and neither does focus, so
       without it the change is silent to a screen reader. On first paint
       nothing has changed, and focusing the h1 there painted a focus ring
       around "See your logo on a cup" the moment anyone opened the page. */
    var head = $('#s-' + id + ' h1[tabindex="-1"]');
    if (head) head.focus();

    /* THE OFFSET IS IN CSS, NOT HERE.

       Canopy's header is position:sticky and around 144px tall, so scrolling
       this section to the top of the viewport parks its first 144px - the step
       rail and the "Back to cups" link - underneath the header. It reads as
       the page having loaded slightly too far down with the back link missing,
       which is exactly how it was reported.

       scrollIntoView rather than a computed window.scrollTo on purpose: the
       offset is `scroll-margin-top` on .hcf-mb in hcf-mockup-builder.css,
       built from Canopy's own --header-height custom property. The browser
       does the arithmetic, the number is not duplicated in two languages, and
       it follows the header if the header ever changes height. */
    ROOT.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function renderRail() {
    var rail = $('#mbRail');
    if (!rail) return;

    var current = SCREENS.indexOf(state.screen); // 0-based
    rail.innerHTML = '';

    RAIL_LABELS.forEach(function (label, i) {
      var done = i < current;
      var here = i === current;

      /* THE RAIL IS THE QUOTE BUILDER'S, NOT OURS.
         This markup deliberately reproduces its step rail exactly - .step
         elements carrying a data-state, each with an .n badge inside - so that
         hcf-quote-builder.css styles this rail with no CSS of our own and the
         two tools cannot drift apart.
           current -> orange badge, white number, ink label  (you are here)
           done    -> black badge, white number, grey label  (finished)
           todo    -> pale badge, grey number, grey label    (not yet)
         Do not rename anything here or style it locally. If the rail changes,
         it changes in that stylesheet and both tools move together. */
      var item = document.createElement(done ? 'button' : 'div');
      item.className = 'step';
      item.setAttribute('data-state', here ? 'current' : (done ? 'done' : 'todo'));

      var num = document.createElement('span');
      num.className = 'n';
      num.textContent = String(i + 1);

      var text = document.createElement('span');
      text.textContent = label;

      item.appendChild(num);
      item.appendChild(text);

      /* Completed steps are buttons so somebody can go back and change their
         mind. Everything else is a div - a clickable-looking thing that does
         nothing is worse than plain text. */
      if (done) {
        item.type = 'button';
        item.addEventListener('click', function () { show(SCREENS[i]); });
      }
      if (here) item.setAttribute('aria-current', 'step');

      rail.appendChild(item);
    });

    /* The rail is meaningless once the request is sent. */
    rail.hidden = (state.screen === 'm4');
  }

  // ---------------------------------------------------------------------------
  // Screen 1 - pick a cup
  // ---------------------------------------------------------------------------

  function bindCups() {
    $$('[data-cup]').forEach(function (tile) {
      tile.addEventListener('click', function () {
        state.cup = tile.getAttribute('data-cup');
        state.cupLabel = tile.getAttribute('data-cup-label') || state.cup;
        paintCups();
        save();
      });
    });
  }

  function paintCups() {
    $$('[data-cup]').forEach(function (tile) {
      var on = tile.getAttribute('data-cup') === state.cup;
      tile.classList.toggle('on', on);
      tile.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    /* Continue stays disabled until there is something to continue with. It is
       rendered disabled in Liquid too, so it is never briefly live before this
       runs, and a returning customer with a saved pick finds it already on. */
    var next = $('#mbToM2');
    if (next) next.disabled = !state.cup;
  }

  // ---------------------------------------------------------------------------
  // Screen 2 - style direction, notes, pricing opt-in
  // ---------------------------------------------------------------------------

  function bindVibes() {
    $$('[data-vibe]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var v = chip.getAttribute('data-vibe');
        // Tapping the selected chip again clears it. Every one of these is
        // optional, so there has to be a way back to "none of these".
        state.vibe = (state.vibe === v) ? null : v;
        paintVibes();
        save();
      });
    });

    var notes = $('#mbNotes');
    if (notes) {
      notes.value = state.notes;
      notes.addEventListener('input', function () {
        state.notes = notes.value;
        save();
      });
    }

    var optIn = $('#mbPricing');
    if (optIn) {
      optIn.checked = state.wantsPricing;
      optIn.addEventListener('change', function () {
        state.wantsPricing = optIn.checked;
        // Clear the quantity when the opt-in goes off, so an unticked box can
        // never ship a stale quantity to HubSpot.
        if (!state.wantsPricing) state.qty = null;
        paintPricing();
        save();
      });
    }

    $$('[data-qty]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var q = chip.getAttribute('data-qty');
        state.qty = (state.qty === q) ? null : q;
        paintPricing();
        save();
      });
    });
  }

  function paintVibes() {
    $$('[data-vibe]').forEach(function (chip) {
      var on = chip.getAttribute('data-vibe') === state.vibe;
      chip.classList.toggle('on', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function paintPricing() {
    var panel = $('#mbQtyPanel');
    if (panel) panel.hidden = !state.wantsPricing;

    $$('[data-qty]').forEach(function (chip) {
      var on = chip.getAttribute('data-qty') === state.qty;
      chip.classList.toggle('on', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  // ---------------------------------------------------------------------------
  // Screen 3 - the HubSpot form
  // ---------------------------------------------------------------------------

  var HS = {
    portal: ROOT.getAttribute('data-hs-portal') || '',
    form: ROOT.getAttribute('data-hs-form') || '',
    region: ROOT.getAttribute('data-hs-region') || 'na1',
    fields: {
      cup: ROOT.getAttribute('data-hsf-cup') || '',
      vibe: ROOT.getAttribute('data-hsf-vibe') || '',
      notes: ROOT.getAttribute('data-hsf-notes') || '',
      pricing: ROOT.getAttribute('data-hsf-pricing') || '',
      qty: ROOT.getAttribute('data-hsf-qty') || ''
    }
  };

  var hsForm = null;        // the V4 form instance, once it reports ready
  var mounted = false;
  var submitted = false;    // guards against a double-fire of the submit event
  var READY_TIMEOUT = 20000;

  /* The skeleton that stands in for the form while the embed is fetching. It
     is hidden the moment the form reports ready, and also by the fallback, so
     there is never a state where both a placeholder and a real form are up. */
  function hideLoading() {
    var loading = $('#mbFormLoading');
    if (loading) loading.hidden = true;
  }

  /* Hides the whole CARD, not just the mount point. The card also carries the
     fine print under the button, and hiding only the mount point would leave
     an empty white box holding nothing but legal text next to the phone
     number. Falls back to the mount point if the card is ever removed. */
  function showFallback() {
    var card = $('#mbFormCard') || $('#mbFormTarget');
    var fallback = $('#mbFormFallback');
    hideLoading();
    if (fallback) fallback.hidden = false;
    if (card) card.hidden = true;
  }

  /* The portal-specific embed script. Note this is NOT forms/embed/v2.js: that
     is the legacy API, and while it currently detects a modern form and
     forwards to this same script, relying on that shim is one HubSpot release
     away from breaking. Load what the form's own embed code specifies. */
  function loadHubSpotScript() {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[data-hcf-hsforms]')) return resolve();
      var s = document.createElement('script');
      s.src = 'https://js.hsforms.net/forms/embed/' + encodeURIComponent(HS.portal) + '.js';
      s.defer = true;
      s.setAttribute('data-hcf-hsforms', '');
      s.onload = function () { resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  /* Called on the way OUT of screen 1 and again on the way IN to screen 3.
     The first call does the real work; the second is a re-sync, because by
     then the customer has actually answered screen 2. */
  function mountForm() {
    if (mounted) { syncFields(); return; }

    var target = $('#mbFormTarget');
    if (!target) return;

    if (!HS.portal || !HS.form) {
      warn('No HubSpot portal/form ID set. Screen 3 is showing the email ' +
           'fallback. Set both in the theme editor to turn the form on.');
      showFallback();
      return;
    }

    mounted = true;
    var fallback = $('#mbFormFallback');
    if (fallback) fallback.hidden = true;
    var card = $('#mbFormCard');
    if (card) card.hidden = false;

    var loading = $('#mbFormLoading');
    if (loading) loading.hidden = false;

    /* The embed script scans for this div and renders the form into it. The
       attribute names are HubSpot's, straight from the embed code they give
       you in Share > Embed code. Appended BEFORE the script is requested, so
       the frame is already in the document whichever order the two resolve
       in - though the script does also pick up frames added after it loads,
       which is what makes the early mount safe to reorder if it ever needs
       to be. */
    var frame = document.createElement('div');
    frame.className = 'hs-form-frame';
    frame.setAttribute('data-region', HS.region);
    frame.setAttribute('data-form-id', HS.form);
    frame.setAttribute('data-portal-id', HS.portal);
    target.appendChild(frame);

    loadHubSpotScript().catch(function (err) {
      warn('HubSpot embed script failed to load', err);
      showFallback();
    });

    /* A form that has not rendered in twenty seconds is not going to. Rather
       than leave somebody staring at an empty box at the end of a flow they
       have already invested in, give them the phone number. */
    window.setTimeout(function () {
      if (!hsForm) {
        warn('HubSpot form never reported ready; showing the fallback.');
        showFallback();
      }
    }, READY_TIMEOUT);
  }

  /* Is this event about OUR form? The page also runs HubSpot's chat widget, and
     a portal can have several forms on one page in future. */
  function formFromEvent(e) {
    var f = null;
    try {
      f = window.HubspotFormsV4 && window.HubspotFormsV4.getFormFromEvent(e);
    } catch (err) { return null; }
    if (!f || typeof f.setFieldValue !== 'function') return null;
    try {
      if (typeof f.getFormId === 'function' && f.getFormId() !== HS.form) return null;
    } catch (err) { /* if it will not tell us, assume it is ours */ }
    return f;
  }

  /* Writes the answers from screens 1 and 2 onto the embedded form.

     The name map is the important part. The form's fields are object-prefixed
     (`0-1/mockup_cup_type`), and setFieldValue with a bare name fails silently.
     Rather than hardcode `0-1/`, read the real names back and index them by
     whatever follows the last slash. If HubSpot ever changes the prefix, or a
     field is moved to another object, this keeps working. */
  function syncFields() {
    if (!hsForm) return;

    Promise.resolve(hsForm.getFormFieldValues()).then(function (fields) {
      var map = {};
      (fields || []).forEach(function (f) {
        if (f && typeof f.name === 'string') {
          map[f.name.split('/').pop()] = f.name;
        }
      });

      function set(bare, value) {
        if (!bare) return;
        var real = map[bare] || bare;
        try {
          hsForm.setFieldValue(real, value == null ? '' : String(value));
        } catch (err) {
          warn('could not set ' + real + ' on the HubSpot form', err);
        }
      }

      set(HS.fields.cup, state.cupLabel || state.cup || '');
      set(HS.fields.vibe, state.vibe || 'No preference');
      set(HS.fields.notes, state.notes || '');

      /* Lowercase on purpose. mockup_wants_pricing is a dropdown whose option
         INTERNAL values are `yes` and `no`; the capitalised versions are only
         the labels HubSpot displays. A value that is not on the option list is
         rejected, so "Yes" would arrive empty. Tokens still render the label,
         so the ticket description reads "Yes" either way. */
      set(HS.fields.pricing, state.wantsPricing ? 'yes' : 'no');

      set(HS.fields.qty, state.wantsPricing ? (state.qty || 'Not sure') : '');
    }).catch(function (err) {
      warn('could not read the HubSpot form field names', err);
    });
  }

  /* Registered once, at init, rather than at mount time. The ready event can
     fire before any code that runs after mountForm() gets a chance to listen. */
  function bindHubSpotEvents() {
    window.addEventListener(HS_EVENT_READY, function (e) {
      var f = formFromEvent(e);
      if (!f) return;
      hsForm = f;
      hideLoading();
      syncFields();
    });

    /* THE HANDOFF TO OUR CONFIRMATION SCREEN.

       Bound to the SUCCESS event specifically, not to a submit attempt, so a
       submission HubSpot rejects leaves the customer on the form with
       HubSpot's own error message rather than on a confirmation screen that
       lies to them.

       Showing screen 4 is also what hides HubSpot's grey "Form submitted"
       panel: that panel lives inside the iframe and cannot be styled or
       suppressed from here, but screen 3 goes display:none the instant this
       fires, and the iframe goes with it.

       Deliberately NOT filtered through formFromEvent: on submit the instance
       is sometimes already torn down, and a confirmation screen that fails to
       appear is worse than one that appears for a form we did not strictly
       verify. This page only ever renders one form.

       ORDER MATTERS ON THE LAST THREE LINES. show() calls save(), so clearing
       localStorage before it just gets the blob written straight back - which
       is what used to happen, leaving a submitted customer's cup, style and
       notes in storage to be restored on their next visit. Clear LAST. */
    window.addEventListener(HS_EVENT_SUBMIT_OK, function (e) {
      if (submitted) return;
      submitted = true;
      renderRecap();   // reads state into the DOM while it is still populated
      show('m4');
      clearSaved();
    });

    /* Nothing to do in the UI - HubSpot renders its own error inside the
       iframe - but a failed submit that leaves no trace anywhere is the kind
       of thing that gets reported as "the form just does nothing". */
    window.addEventListener(HS_EVENT_SUBMIT_FAIL, function (e) {
      warn('HubSpot reported a failed submission. The customer is still on ' +
           'the form with HubSpot\'s own error message.');
    });
  }

  // ---------------------------------------------------------------------------
  // Screen 4 - confirmation recap
  // ---------------------------------------------------------------------------

  function renderRecap() {
    var box = $('#mbRecap');
    if (!box) return;

    var rows = [];
    if (state.cupLabel) rows.push(['Cup', state.cupLabel]);
    if (state.vibe) rows.push(['Style', state.vibe]);
    if (state.notes) rows.push(['Your notes', state.notes]);
    if (state.wantsPricing) rows.push(['Pricing', 'Yes, around ' + (state.qty || 'a quantity to be confirmed')]);

    if (!rows.length) {
      box.hidden = true;
      return;
    }

    box.hidden = false;
    box.innerHTML = rows.map(function () {
      return '<div class="mb-recap__row">' +
               '<span class="mb-recap__k"></span>' +
               '<span class="mb-recap__v"></span>' +
             '</div>';
    }).join('');

    /* Filled in as text nodes rather than interpolated into the HTML above,
       because state.notes is whatever the customer typed. */
    Array.prototype.forEach.call(box.querySelectorAll('.mb-recap__row'), function (row, i) {
      row.querySelector('.mb-recap__k').textContent = rows[i][0];
      row.querySelector('.mb-recap__v').textContent = rows[i][1];
    });
  }

  // ---------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------

  function bindNav() {
    /* Screen 1 -> 2. This is also where the HubSpot embed is started, for the
       reasons in the header. Deliberately not on tile click: somebody who taps
       three cups before deciding should not fire three mounts, and mountForm
       guards on `mounted` anyway. */
    var toM2 = $('#mbToM2');
    if (toM2) toM2.addEventListener('click', function () {
      if (!state.cup) return;   // belt and braces; the button is disabled too
      show('m2');
      mountForm();
    });

    var toM3 = $('#mbToM3');
    if (toM3) toM3.addEventListener('click', function () {
      show('m3');
      mountForm();              // already mounted by now: this is the re-sync
    });

    $$('[data-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        show(btn.getAttribute('data-back'));
      });
    });

    /* "Request another mockup" on the confirmation screen. The form itself is
       left mounted and is not reset - HubSpot owns its own state, and somebody
       sending a second request will land back on a form that still knows who
       they are, which is a convenience rather than a bug. */
    var again = $('#mbAgain');
    if (again) again.addEventListener('click', function () {
      state.cup = null;
      state.cupLabel = '';
      state.vibe = null;
      state.notes = '';
      state.wantsPricing = false;
      state.qty = null;
      submitted = false;
      clearSaved();
      paintCups();
      paintVibes();
      paintPricing();
      var notes = $('#mbNotes');
      if (notes) notes.value = '';
      var optIn = $('#mbPricing');
      if (optIn) optIn.checked = false;
      show('m1');
    });
  }

  function init() {
    restore();
    bindCups();
    bindVibes();
    bindNav();
    bindHubSpotEvents();
    paintCups();
    paintVibes();
    paintPricing();

    /* Always opens on screen one, even with saved state. Dropping somebody
       straight back onto the form screen of a flow they half-remember is
       disorienting; their picks are still there, they just walk past them.

       noScroll: this is the page arriving, not a move between screens. See the
       note on show(). */
    show('m1', { initial: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();